import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useMarina } from "src/contexts/MarinaContext"
import { toTaxPercent } from "src/main/Billing/Items/helpers"
import AmountInputField from "src/main/Contracts/ContractsForm/shared/AmountInputField"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"
import ReloadableWidget from "src/components/ReloadableWidget"
import TaxRateDisplayInput from "src/components/TaxRateDisplayInput"

const LateFeeSettingsModal = ({
  isFetchingLateFeeProducts,
  isOpen,
  lateFeeProducts = [],
  onSave,
  onClose,
  selectedLateFee,
  useNewTaxExperience,
}) => {
  const { marinaSlug } = useMarina()
  const {
    reset,
    register,
    handleSubmit,
    watch,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm({
    mode: "all",
    defaultValues: selectedLateFee ?? {
      name: "",
      amountType: "percent",
      amount: "",
      productId: "",
      gracePeriodDays: "",
    },
  })
  const [displayTaxRate, setDisplayTaxRate] = useState("")
  const [taxes, setTaxes] = useState([])

  const [productId, selectedAmountType] = watch(["productId", "amountType"])

  useEffect(() => {
    if (lateFeeProducts && productId) {
      const lateFeeProduct = lateFeeProducts.find(
        (product) => product.id === productId
      )

      if (lateFeeProduct) {
        setValue("name", lateFeeProduct.name)
        const taxRate = useNewTaxExperience
          ? lateFeeProduct.combined_tax_rate_display
          : `${toTaxPercent(lateFeeProduct.tax_rate)}%`
        const taxes = useNewTaxExperience ? lateFeeProduct.taxes : []
        setDisplayTaxRate(taxRate)
        setTaxes(taxes)
      }
    }
  }, [lateFeeProducts, productId, selectedLateFee])

  const validateLateFeeAmount = (selectedAmountType) => (amount) => {
    const decimalPlacesString = amount.split(".")[1]
    const numericAmount = new Decimal(amount)

    if (selectedAmountType === "percent") {
      if (numericAmount.lte(0) || numericAmount.gt(100)) {
        return "Please specify a percent amount greater than 0 but not exceeding 100."
      }
      if (decimalPlacesString?.length > 3) {
        return "Please specify no more than 3 decimal places."
      }
    } else {
      if (numericAmount.lte(0)) {
        return "Please specify an amount greater than 0."
      }
      if (decimalPlacesString?.length > 2) {
        return "Please specify no more than 2 decimal places."
      }
    }
  }

  const renderItemsPageLink = (productId) => {
    const url = productId
      ? `/manage/${marinaSlug}/items/${productId}/edit`
      : `/manage/${marinaSlug}/items`

    return (
      <a
        className="text-link font-semibold"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {" "}
        items screen
      </a>
    )
  }

  const onSaveClick = (data) => {
    onSave(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      afterLeave={reset}
      size="mediumFixed"
    >
      <Modal.Header>
        <div className="text-lg font-semibold">Late Fee Settings</div>
        Late fees can be configured in the {renderItemsPageLink()}.
      </Modal.Header>
      <Modal.Body>
        <ReloadableWidget isLoading={isFetchingLateFeeProducts} showOverlay>
          <Form>
            <div className="flex flex-col gap-6">
              <div>
                <Form.Label htmlFor="late-fee-product-select">
                  Late fee
                </Form.Label>
                <Form.Select
                  id="late-fee-product-select"
                  {...register("productId", {
                    required: "Please select a late fee item.",
                  })}
                  hasErrors={Boolean(errors.productId)}
                >
                  <option disabled value="">
                    Select a fee item
                  </option>
                  {lateFeeProducts.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
                {errors?.productId ? (
                  <Form.Error>{errors.productId.message}</Form.Error>
                ) : null}
              </div>
              <div>
                <div className="flex flex-row gap-2">
                  <div className="w-1/2">
                    <Form.Label htmlFor="late-fee-type-select">Type</Form.Label>
                    <Form.Select
                      id="late-fee-type-select"
                      {...register("amountType", {
                        onChange: () => {
                          if (errors.amount) {
                            clearErrors("amount")
                          }
                        },
                      })}
                    >
                      <option value="percent">
                        Percent of invoice balance
                      </option>
                      <option value="currency">Flat amount</option>
                    </Form.Select>
                  </div>
                  <div className="w-1/2">
                    <Form.Label htmlFor="late-fee-amount-textfield">
                      Amount
                    </Form.Label>
                    <AmountInputField
                      id="late-fee-amount-textfield"
                      registeredField={register("amount", {
                        required: "Please specify an amount.",
                        validate: validateLateFeeAmount(selectedAmountType),
                      })}
                      amountType={selectedAmountType}
                      hasErrors={Boolean(errors.amount)}
                    />
                    {errors?.amount ? (
                      <Form.Error>{errors.amount.message}</Form.Error>
                    ) : null}
                  </div>
                </div>
              </div>
              <div>
                <Form.Label htmlFor="late-fee-grace-period-days-textfield">
                  Apply to invoices after
                </Form.Label>
                <Form.IconTextField
                  id="late-fee-grace-period-days-textfield"
                  position="right"
                  icon="days past due date"
                  {...register("gracePeriodDays", {
                    required:
                      "Please specify the number of days after which the late fee will be applied",
                    min: {
                      value: 1,
                      message:
                        "Please specify a whole number between 1 and 1000.",
                    },
                    max: {
                      value: 1000,
                      message:
                        "Please specify a whole number between 1 and 1000.",
                    },
                    pattern: {
                      value: /^\d+$/, // digits 0-9 only, no decimal support
                      message:
                        "Please specify a whole number between 1 and 1000.",
                    },
                  })}
                  type="number"
                  hasErrors={Boolean(errors.gracePeriodDays)}
                />
                {errors?.gracePeriodDays ? (
                  <Form.Error>{errors.gracePeriodDays.message}</Form.Error>
                ) : null}
                <div className="text-muted pt-2">
                  Fees will be applied after the specified period. For example,
                  if an invoice is due on the 1st and you input 7 days, a late
                  fee will be added the morning of the 8th.
                </div>
              </div>
              <div>
                <TaxRateDisplayInput
                  combinedTaxRateDisplay={displayTaxRate}
                  taxes={taxes}
                  label="Applicable tax rate"
                />
                <div className="text-muted pt-2">
                  Tax rate comes from the Late fee item selected above. If you
                  wish to make an update to this value (that will apply
                  globally) please do so from the{" "}
                  {renderItemsPageLink(productId)}.
                </div>
              </div>
            </div>
          </Form>
        </ReloadableWidget>
      </Modal.Body>
      <Modal.Footer>
        <div className="mt-2 flex flex-row justify-end gap-4">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit(onSaveClick)}>
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

LateFeeSettingsModal.propTypes = {
  isFetchingLateFeeProducts: PropTypes.bool.isRequired,
  lateFeeProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      combined_tax_rate_display: PropTypes.string,
      name: PropTypes.string.isRequired,
      tax_rate: PropTypes.string,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  selectedLateFee: PropTypes.shape({
    amount: PropTypes.string.isRequired,
    amountType: PropTypes.oneOf(["percent", "currency"]).isRequired,
    name: PropTypes.string.isRequired,
    gracePeriodDays: PropTypes.string.isRequired,
    productId: PropTypes.string.isRequired,
  }),
  useNewTaxExperience: PropTypes.bool.isRequired,
}

export default LateFeeSettingsModal
